import { ModalityLabel } from '@dynamika-shared/types/imagingLabels';
import {
  CommonInventoryType,
  UploadFileTypeEnum,
} from '@dynamika-shared/types/inventory';
import { CollectionTagEnum } from '@dynamika-shared/types/pmTaskDefinitions';

export const groupLabelsByFileType = (modalityLabels: ModalityLabel[]) => {
  return modalityLabels.reduce(
    (acc, modality) => {
      modality.labels.forEach(label => {
        const fileType = label.dataType;

        acc[fileType] = acc[fileType] || {};

        acc[fileType][modality.modalityName] =
          acc[fileType][modality.modalityName] || [];

        acc[fileType][modality.modalityName].push(label);
      });

      return acc;
    },
    {} as Record<string, Record<string, CommonInventoryType[]>>
  );
};

const groupLabelsByModality = (
  modalityLabels: ModalityLabel[],
  isOffered: (label: CommonInventoryType) => boolean = () => true
) =>
  modalityLabels.reduce(
    (acc, { modalityName, labels }) => {
      const offered = labels.filter(isOffered);

      if (offered.length) {
        acc[modalityName] = [...(acc[modalityName] ?? []), ...offered];
      }

      return acc;
    },
    {} as Record<string, CommonInventoryType[]>
  );

/**
 * QCT is a label data type the BE already returns but the FE enum does not list yet
 *
 * Technical
 * ---------
 * `/v3/api-docs` declares `dataType` as DICOM | DXA | DICOMIZED_CSV | DICOMIZED_PDF |
 * HRPQCT | QCT | MISC | MEDIA, and dev studies carry labels such as "QCT Hip" with
 * `dataType: 'QCT'` — but `UploadFileTypeEnum` has no `QCT` member. Adding it belongs
 * to D7-2207, so this compares against the literal to avoid colliding with that branch.
 */
const QCT_DATA_TYPE = 'QCT';

/**
 * Which labels each non-imaging source offers
 *
 * Plain terms
 * -----------
 * 1. JSON is the numeric read-out of a QCT workstation, so it is only ever filed
 *    against a QCT body-part label — offering anything else would invite a task
 *    definition that can never match.
 * 2. A patient File can be attached to almost anything, so the reader may pick any
 *    label except DXA ones, which arrive through their own dedicated pipeline.
 *
 * Technical
 * ---------
 * Product decision (Vitaliy, 2026-09-15), explicitly interim: JSON narrows to
 * `dataType === 'QCT'`, FILE excludes `DXA`. Expected to be revisited once the BE
 * exposes which labels a given source may legitimately reference.
 */
const sourceLabelFilters: Record<
  string,
  (label: CommonInventoryType) => boolean
> = {
  [CollectionTagEnum.JSON]: ({ dataType }) =>
    (dataType as string) === QCT_DATA_TYPE,
  [CollectionTagEnum.FILE]: ({ dataType }) =>
    dataType !== UploadFileTypeEnum.DXA,
};

/**
 * Which labels a Task Definition "Source" may point at
 *
 * Plain terms
 * -----------
 * A task definition says "watch this kind of data for this body part". For imaging
 * sources — DICOM, DXA — the study inventory already splits labels by that same kind,
 * so we only offer the labels of the picked kind. "File" and "JSON" are different:
 * they are not a kind of image, they are extra data attached to a scan the study
 * already has. A JSON read-out of a QCT workstation, for instance, is filed under the
 * ordinary "QCT Hip" label — so those two sources get their own shortlist instead
 * (see `sourceLabelFilters`).
 *
 * Technical
 * ---------
 * `collectionType` (`CollectionTagEnum`: TASK, DICOM, DXA, MEASUREMENT, FILE, JSON) and
 * the label's `dataType` (`UploadFileTypeEnum`: DICOM, DXA, DICOMIZED_*, HRPQCT, QCT,
 * MISC, MEDIA) are two different BE enums that merely overlap on DICOM/DXA. FILE and
 * JSON exist only in the first one, so a `groupLabelsByFileType(...)[collectionType]`
 * lookup can never resolve for them. Verified against a live task definition whose
 * condition is `{ collectionType: 'JSON', propertyName: 'LABEL_ID',
 * expected: { type: '.UuidSingle', expected: <QCT Hip label id> } }`.
 */
export const getLabelsForCollectionType = (
  modalityLabels: ModalityLabel[],
  collectionType?: string
): Record<string, CommonInventoryType[]> | undefined => {
  const isLabelDataType =
    !!collectionType &&
    Object.keys(UploadFileTypeEnum).includes(collectionType);

  if (isLabelDataType) {
    return groupLabelsByFileType(modalityLabels)[
      collectionType as UploadFileTypeEnum
    ];
  }

  return groupLabelsByModality(
    modalityLabels,
    collectionType ? sourceLabelFilters[collectionType] : undefined
  );
};
